import React from 'react';
import { OfficeId, OfficePanelId } from '../../lib/types';
import { Settings } from '../../lib/types';
import { getOffice, OFFICE_PANEL_LABELS } from '../../lib/config';
import Panel from '../Panel';
import ChatPanel from '../chat/ChatPanel';
import KanbanBoard from '../kanban/KanbanBoard';
import GanttChartFrappe from '../gantt/GanttChartFrappe';
import CalendarView from '../calendar/CalendarView';
import TeamGrid from '../agents/TeamGrid';
import ScaffoldPanel from './ScaffoldPanel';
import WindsorPanel from '../developments/WindsorPanel';

interface OfficeScreenProps {
  officeId: OfficeId;
  activePanel: OfficePanelId;
  settings: Settings;
  onNavigate: (panel: OfficePanelId) => void;
  onOpenAgent: (agentId: string) => void;
  onBack: () => void;
}

/**
 * OfficeScreen — renders the active panel for a given office.
 * The Panel chrome and OfficeDock are handled by Desktop.tsx.
 */
export default function OfficeScreen({
  officeId,
  activePanel,
  settings,
  onNavigate,
  onOpenAgent,
  onBack,
}: OfficeScreenProps) {
  const office = getOffice(officeId);
  if (!office) return null;

  const officeSettings = settings.offices?.[officeId] || {};
  const panelTitle = `${office.name} — ${OFFICE_PANEL_LABELS[activePanel]}`;

  return (
    <Panel
      title={panelTitle}
      onClose={onBack}
      data-testid={`office-panel-${officeId}`}
    >
      <OfficePanelContent
        officeId={officeId}
        panel={activePanel}
        office={office}
        officeSettings={officeSettings}
        settings={settings}
        onOpenAgent={onOpenAgent}
      />
    </Panel>
  );
}

// ── Office Panel Router ───────────────────────────────────────────────────────

interface OfficePanelContentProps {
  officeId: OfficeId;
  panel: OfficePanelId;
  office: ReturnType<typeof getOffice>;
  officeSettings: { chatWebhookUrl?: string; chatPollUrl?: string };
  settings: Settings;
  onOpenAgent: (agentId: string) => void;
}

/** Helper: resolve an office app endpoint URL from settings.officeApps */
function appUrl(settings: Settings, officeId: string, key: string): string | undefined {
  return settings.officeApps?.[officeId]?.[key] || undefined;
}

function OfficePanelContent({
  officeId, panel, office, officeSettings, settings, onOpenAgent,
}: OfficePanelContentProps) {
  switch (panel) {
    case 'chat':
      return (
        <ChatPanel
          conversationId={`office:${officeId}`}
          agentName={office?.name || officeId}
          webhookUrl={officeSettings.chatWebhookUrl}
          pollUrl={officeSettings.chatPollUrl}
        />
      );
    case 'kanban':
      return <KanbanBoard officeId={officeId} />;
    case 'gantt':
      return <GanttChartFrappe officeId={officeId} />;
    case 'calendar':
      return <CalendarView />;
    case 'team':
      return <TeamGrid officeId={officeId} onSelectAgent={onOpenAgent} />;

    // ── Office-specific scaffold panels ────────────────────────────────────
    case 'budget':
      return (
        <ScaffoldPanel
          title="Budget"
          columns={['Item', 'Category', 'Amount', 'Due Date', 'Status']}
          endpointUrl={appUrl(settings, officeId, 'budgetApiUrl')}
          endpointKey={`officeApps.${officeId}.budgetApiUrl`}
        />
      );
    case 'projects':
      return (
        <ScaffoldPanel
          title="Projects"
          columns={['Project Name', 'Client', 'Phase', 'Start', 'End', 'Status']}
          endpointUrl={appUrl(settings, officeId, 'projectsApiUrl')}
          endpointKey={`officeApps.${officeId}.projectsApiUrl`}
        />
      );
    case 'sacap':
      return (
        <ScaffoldPanel
          title="SACAP Tracker"
          columns={['Project', 'Submission Type', 'Submitted', 'Status', 'Notes']}
          endpointUrl={appUrl(settings, officeId, 'sacapApiUrl')}
          endpointKey={`officeApps.${officeId}.sacapApiUrl`}
        />
      );
    case 'deals':
      return (
        <ScaffoldPanel
          title="Deal Pipeline"
          columns={['Property', 'Client', 'Stage', 'Value', 'Days Active', 'Agent']}
          endpointUrl={appUrl(settings, officeId, 'dealsApiUrl')}
          endpointKey={`officeApps.${officeId}.dealsApiUrl`}
        />
      );
    case 'leads':
      return (
        <ScaffoldPanel
          title="Leads"
          columns={['Name', 'Source', 'Status', 'Property Interest', 'Last Contact']}
          endpointUrl={appUrl(settings, officeId, 'leadsApiUrl')}
          endpointKey={`officeApps.${officeId}.leadsApiUrl`}
        />
      );
    case 'hopper':
      return (
        <ScaffoldPanel
          title="Hopper — Development Pipeline"
          columns={['Site', 'Stage', 'Zoning', 'Units', 'Value', 'Next Action']}
          endpointUrl={appUrl(settings, officeId, 'hopperApiUrl')}
          endpointKey={`officeApps.${officeId}.hopperApiUrl`}
        />
      );
    case 'feasibility':
      return (
        <ScaffoldPanel
          title="Feasibility Models"
          columns={['Site', 'Revenue', 'Cost', 'Margin', 'IRR', 'Status']}
          endpointUrl={appUrl(settings, officeId, 'feasibilityApiUrl')}
          endpointKey={`officeApps.${officeId}.feasibilityApiUrl`}
        />
      );
    case 'windsor':
      return <WindsorPanel />;
    case 'properties':
      return (
        <ScaffoldPanel
          title="Properties"
          columns={['Address', 'Type', 'Tenants', 'Rent', 'Lease End', 'Status']}
          endpointUrl={appUrl(settings, officeId, 'propertiesApiUrl')}
          endpointKey={`officeApps.${officeId}.propertiesApiUrl`}
        />
      );
    case 'tenants':
      return (
        <ScaffoldPanel
          title="Tenants"
          columns={['Name', 'Property', 'Lease Start', 'Lease End', 'Rent', 'Status']}
          endpointUrl={appUrl(settings, officeId, 'tenantsApiUrl')}
          endpointKey={`officeApps.${officeId}.tenantsApiUrl`}
        />
      );
    case 'compliance':
      return (
        <ScaffoldPanel
          title="Compliance"
          columns={['Item', 'Property', 'Due Date', 'Status', 'Notes']}
          endpointUrl={appUrl(settings, officeId, 'complianceApiUrl')}
          endpointKey={`officeApps.${officeId}.complianceApiUrl`}
        />
      );
    default:
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          <p className="text-sm">Panel not implemented</p>
        </div>
      );
  }
}
